import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Dropzone from 'react-dropzone';
import Lightbox from 'react-images';
import {
  compose,
  setDisplayName,
} from 'recompose';
import {
  compact,
  concat,
  differenceBy,
  get,
  isEmpty,
  orderBy,
  sortedUniqBy,
} from 'lodash';
import PubNubReact from 'pubnub-react';
import axios from 'axios';
import isImageUrl from 'is-image-url';
import * as linkify from 'linkifyjs';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Add from '@material-ui/icons/Add';
import Chat from '@material-ui/icons/Chat';
import ArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import ArrowRight from '@material-ui/icons/KeyboardArrowRight';
import Remove from '@material-ui/icons/Remove';
import Send from '@material-ui/icons/Send';

import immutableToJS from 'helpers/immutableToJS';
import {
  dispatchRestMaker,
  toLongestDateFormat,
  scrollTo,
  toShortTimeFormat,
} from 'helpers/utils';
import {
  Avatar,
  IconButton,
  injectIntl,
  NotificationBadge,
} from 'components';
import buyersActions from 'redux/buyers/actions';
import { listPresences, sendMessage, messageRead } from 'redux/chats/actions';
import { selectState } from 'redux/selectors';
import membersActions from 'redux/members/actions';
import sellersActions from 'redux/sellers/actions';

const styles = () => ({
  backward: { color: '#2196f3' },
  badge: {
    bottom: 50,
    position: 'absolute',
    right: 10,
  },
  badgeSpan: {
    backgroundColor: '#ff602e',
    color: 'white',
  },
  badgeSpanActive: {
    backgroundColor: '#f44336',
    color: 'white',
  },
  chatIcon: {
    background: '#4695ec',
    '&:hover': {
      background: '#4695ec',
    },
  },
  disabledSendButton: { background: 'gray !important' },
  forward: {
    color: '#2196f3',
    height: 20,
    width: 20,
  },
  leftBadge: {
    left: 43,
    position: 'absolute',
    top: 13,
  },
  minimizeBtn: {
    color: '#2196f3',
    height: 20,
    transform: 'scale(1.5)',
    width: 20,
  },
  send: { color: 'white' },
  sendButton: {
    background: '#7298e1',
    borderRadius: 0,
    color: 'grey',
    height: '100%',
    minHeight: 45,
    minWidth: 41,
    padding: '5px 0px 5px 0px',
  },
  topBadge: { marginRight: 20 },
  upload: { color: '#7298e1' },
  uploadButton: {
    color: 'grey',
    height: '100%',
    minHeight: 38,
    minWidth: 35,
    padding: 0,
    paddingBottom: 5,
  },
});

class ChatBox extends Component {
  static contextTypes = {
    currentMember: PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
    currentUser: PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
  };

  static propTypes = {
    buyers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    })),
    classes: PropTypes.shape({}).isRequired,
    presences: PropTypes.arrayOf(PropTypes.string).isRequired,
    sellers: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ]),
    })),
    tokenInfo: PropTypes.shape({}).isRequired,

    formatMessage: PropTypes.func.isRequired,
    listBuyers: PropTypes.func.isRequired,
    listMembers: PropTypes.func.isRequired,
    listPresences: PropTypes.func.isRequired,
    listSellers: PropTypes.func.isRequired,
    sendMessage: PropTypes.func.isRequired,
    messageRead: PropTypes.func.isRequired,
  };

  static defaultProps = {
    buyers: [],
    sellers: [],
  };

  constructor(props, context) {
    super(props, context);
    this.state = {
      chatInput: '',
      contacts: [],
      currentContact: {},
      history: {},
      minimized: true,
      notifications: {},
      notificationTotal: 0,
      showList: true,
      lightboxImages: [],
      currentImage: 0,
      lightboxIsOpen: false,
    };

    if (window.favicon) {
      window.favicon.badge(0);
    }
    // run hereby every 10 seconds.
    this.listPresencesInterval = setInterval(() => {
      props.listPresences();
    }, 10000);

    this.initPubnub(context);

    const { listBuyers, listMembers, listPresences, listSellers, tokenInfo } = props;
    if (tokenInfo && tokenInfo.accessToken) {
      listBuyers();
      listMembers();
      listPresences();
      listSellers();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.buyers !== this.props.buyers || nextProps.sellers !== this.props.sellers) {
      const contacts = this.getContacts(nextProps);
      const unsubscribeContacts = differenceBy(this.state.contacts, contacts, 'id');
      const subscribeContacts = differenceBy(contacts, this.state.contacts, 'id');
      window.pubnub.unsubscribe({
        channels: unsubscribeContacts.map(contact => `dc-${contact.id}`),
      });

      const channels = subscribeContacts.map(contact => `dc-${contact.id}`);

      if (channels.length) {
        window.pubnub.subscribe({
          channels,
          withPresence: false,
        });
      }

      const updateState = {
        contacts,
        history: this.state.history,
        notifications: this.state.notifications || {},
        notificationTotal: this.state.notificationTotal,
      };

      subscribeContacts.forEach((contact) => {
        const channelName = `dc-${contact.id}`;
        window.pubnub.getMessage(channelName, msg => this.onGetMessage(contact.id, msg));
        const chatHistory = updateState.history[contact.id] || [];
        updateState.history = {
          ...updateState.history,
          [contact.id]:
            chatHistory.length
              ? chatHistory
              : compact([get(contact, 'chat.lastMessage')]),
        };
        const newNotificationCount = get(contact, contact.isBuyer ? 'chat.sellerUnreadCount' : 'chat.buyerUnreadCount') || 0;
        updateState.notificationTotal +=
          newNotificationCount - (updateState.notifications[contact.id] || 0);
        updateState.notifications[contact.id] = newNotificationCount;
      });
      this.setState(updateState, this.updateNotificationFav);
    }
  }

  componentWillUnmount() {
    clearInterval(this.listPresencesInterval);
    window.pubnub.unsubscribe({
      channels: this.state.contacts.map(contact => `dc-${contact.id}`),
    });
  }

  onGetMessage = (contactId, msg) => {
    const { messageRead } = this.props;
    const {
      currentContact,
      history,
      notifications,
      notificationTotal,
    } = this.state;
    if (history[`${contactId}-read`]) {
      this.setState({
        history: {
          ...history,
          [contactId]: concat(history[contactId], {
            entry: msg.message,
            timetoken: msg.timetoken / 10000,
          }),
        },
      }, () => {
        if (contactId === currentContact.id) {
          this.scrollToBottom();
        }
      });
    }
    if (contactId === currentContact.id) {
      if (currentContact.type !== 'self') {
        messageRead({ contactId: currentContact.id });
      }
      this.scrollToBottom();
    } else {
      this.setState({
        notifications: {
          ...notifications,
          [contactId]: (notifications[contactId] || 0) + 1,
        },
        notificationTotal: notificationTotal + 1,
      }, this.updateNotificationFav);
    }
  };

  getContacts = (props = this.props) => {
    const { currentUser } = this.context;
    const { formatMessage } = this.props;
    const buyers = props.buyers.map(buyer => ({
      id: buyer.id,
      isBuyer: true,
      chat: buyer.chat,
      profile: buyer.buyerProfile,
    })).filter(buyer => !buyer.isDeleted && !buyer.requestorId);
    const sellers = props.sellers.map(seller => ({
      id: seller.id,
      isBuyer: false,
      chat: seller.chat,
      profile: seller.sellerProfile,
    })).filter(seller => !seller.isDeleted && !seller.requestorId);
    return compact([
      Boolean(currentUser.role) && {
        id: `0-${currentUser.id}`,
        label: formatMessage('YOUR TEAM CHANNEL'),
        type: 'self',
        profile: {
          ...currentUser.account.profile,
          email: currentUser.account.email,
        },
      },
      ...sortedUniqBy(
        orderBy(
          concat(buyers, sellers),
          [
            contact => Boolean(this.state.notifications[contact.id]),
            this.isOnline,
            contact => contact.profile.companyName,
          ],
          [
            'desc',
            'desc',
            'asc',
          ],
        ),
        contact => contact.id,
      ),
    ]);
  };

  getUUID = (context = this.context) => {
    const { currentMember, currentUser } = context;
    return currentMember ? `${currentUser.account.id}-${currentMember.id}` : currentUser.account.id;
  }

  initPubnub = (context = this.context) => {
    const uuid = this.getUUID(context);
    window.pubnub = new PubNubReact({
      publishKey: process.env.PUBNUB_PUBLISH_KEY,
      subscribeKey: process.env.PUBNUB_SUBSCRIBE_KEY,
      uuid,
      setPresenceTimeout: 0,
      setPresenceTimeoutWithCustomInterval: 0,
      heartbeatInterval: 0,
      suppressLeaveEvents: true,
    });
    window.pubnub.init(this);
  };

  handleChange = name => event => this.setState({ [name]: event.target.value });

  handleUploadImages = (images) => {
    const urls = [];
    const uploads = images.map((image, index) => {
      // our formdata
      const formData = new FormData();
      formData.append('file', image);
      formData.append('tags', 'chat-img'); // Add tags for the images - {Array}
      formData.append('upload_preset', 'iyvam6eb'); // Replace the preset name with your own
      formData.append('api_key', '196493794921893'); // Replace API key with your own Cloudinary API key
      formData.append('timestamp', (Date.now() / 1000));

      // Replace cloudinary upload URL with yours
      return axios.post(
        'https://api.cloudinary.com/v1_1/dvohfyl2v/image/upload',
        formData,
        { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => {
          urls[index] = response.data.url;
        });
    });

    // We would use axios `.all()` method to perform concurrent image upload to cloudinary.
    axios.all(uploads).then(() => {
      // ... do anything after successful upload. You can setState() or save the data
      this.sendMessage(urls.join('\n'));
    });
  };

  updateNotificationFav = () => {
    const { notificationTotal } = this.state;
    if (window.favicon) {
      window.favicon.badge(notificationTotal);
    }
  };

  hideChatBox = () => this.setState({ minimized: true });

  isOnline = (contact) => {
    const { presences } = this.props;
    if (contact.type === 'self') return true;
    return presences.indexOf(`dc-${contact.id}`) !== -1;
  };

  readHistory = () => {
    const { messageRead } = this.props;
    const { currentContact, history } = this.state;

    if (history[currentContact.id] && history[`${currentContact.id}-read`]) {
      this.scrollToBottom();
      this.removeNotification();
      return;
    }
    window.pubnub.history({
      channel: `dc-${currentContact.id}`,
      count: 100,
    }).then(({ messages }) => {
      if (currentContact.type !== 'self') {
        messageRead({ contactId: currentContact.id });
      }
      this.setState({
        history: {
          ...history,
          [currentContact.id]: messages.map(message => ({
            ...message,
            timetoken: message.timetoken / 10000,
          })),
          [`${currentContact.id}-read`]: true,
        },
      }, () => {
        this.scrollToBottom();
        this.removeNotification();
      });
    });
  };

  removeNotification = () => {
    const {
      currentContact,
      notificationTotal,
      notifications,
    } = this.state;
    this.setState({
      notificationTotal: notificationTotal - (notifications[currentContact.id] || 0),
      notifications: {
        ...notifications,
        [currentContact.id]: 0,
      },
    }, this.updateNotificationFav);
  }

  scrollToBottom = () => {
    scrollTo('chat-history', { isAnimateScroll: true, topOrBottom: 'bottom' });
  };

  sendMessage = (text) => {
    const { currentMember, currentUser } = this.context;
    const { sendMessage } = this.props;
    const { currentContact } = this.state;
    const message = {
      id: this.getUUID(),
      firstName: currentMember ? get(currentMember, 'profile.firstName') : get(currentUser, 'account.profile.firstName'),
      lastName: currentMember ? get(currentMember, 'profile.lastName') : get(currentUser, 'account.profile.lastName'),
      text,
    };
    this.setState({ chatInput: '' });
    window.pubnub.publish({
      message,
      channel: `dc-${currentContact.id}`,
    });
    if (currentContact.type !== 'self') {
      sendMessage({ contactId: currentContact.id, message });
    }
  };

  showChatBox = () => this.setState({ minimized: false });

  openLightbox = (item) => {
    this.setState({
      currentImage: 0,
      lightboxIsOpen: true,
      lightboxImages: [{
        itemId: 0,
        src: item.href,
      }],
    });
  };

  closeLightbox = () => {
    this.setState({
      lightboxIsOpen: false,
    });
  };

  renderChatRoom = () => (
    <div className="chat-room">
      <Dropzone
        acceptStyle={{
          borderStyle: 'dashed',
          borderWidth: 1,
          opacity: 0.5,
        }}
        className="chat-history"
        disableClick
        id="chat-history"
        multiple
        onDrop={this.handleUploadImages}
        ref={(c) => {
          this.fileInput = c;
        }}
        style={{
          flex: 1,
          overflow: 'auto',
          width: '100%',
        }}
      >
        {this.renderMessage()}
      </Dropzone>
    </div>
  );

  renderContact = (contact) => {
    const { classes } = this.props;
    const lastMessage = this.state.history[contact.id];
    const contactName = contact.profile.companyName || `${contact.profile.firstName} ${contact.profile.lastName}`;
    return (
      <div
        className="contact-list-item"
        key={contact.id}
        onClick={() => this.setState({
            currentContact: contact,
            showList: false,
          }, this.readHistory)
        }
      >
        <Avatar
          src={contact.profile.logoUrl}
          key={contact.profile.email}
          fullName={contactName}
        />
        {Boolean(this.state.notifications[contact.id]) && (
          <NotificationBadge classes={{
            badge: { badge: classes.badgeSpanActive },
            root: { root: classes.leftBadge },
          }}>
            {this.state.notifications[contact.id]}
          </NotificationBadge>
        )}
        <span className={`presence ${this.isOnline(contact) && 'online'}`} />
        <div className="contact-info">
          <div className="header">
            <div className="company-name">
              {contact.label || contactName}
            </div>
            <div className={`last-message-time ${this.state.notifications[contact.id] ? 'new' : ''}`}>
              {
                isEmpty(lastMessage)
                  ? ''
                  : toShortTimeFormat(get(lastMessage, `${lastMessage.length - 1}.timetoken`))
              }
            </div>
          </div>
          <div className="last-message">
            {
              isEmpty(lastMessage)
                ? ''
                : get(lastMessage, `${lastMessage.length - 1}.entry.text`)
            }
          </div>
        </div>
        <div className="forward">
          <ArrowRight classes={{ root: classes.forward }} />
        </div>
      </div>
    );
  };

  renderList = () => {
    const { contacts } = this.state;
    return (
      <div className="contact-list">
        {contacts.map(contact => this.renderContact(contact))}
      </div>
    );
  };

  renderMessage = () => {
    const { currentContact, history } = this.state;
    const nodes = [];
    const messages = (history[currentContact.id] || []);
    messages.forEach((message, index) => {
      const urls = linkify.find(message.entry.text || '');
      const texts = [];
      const remainingText = urls.reduce((current, url) => {
        const urlIndex = current.indexOf(url.value);
        if (urlIndex === -1) return current;
        if (urlIndex > 0) {
          texts.push({
            type: 'text',
            value: current.substr(0, urlIndex),
          });
        }
        if (isImageUrl(url.href)) {
          texts.push({
            ...url,
            href: url.href.replace('http:', window.location.protocol),
            type: 'imageUrl',
          });
        } else {
          texts.push(url);
        }
        return current.substr(urlIndex + url.value.length);
      }, message.entry.text || '');
      const objectClassName = message.entry.id === this.getUUID() ? 'mine' : 'other';
      texts.push({ type: 'text', value: remainingText });
      if (index === 0 || toLongestDateFormat(messages[index - 1].timetoken)
        !== toLongestDateFormat(message.timetoken)) {
        nodes.push(
          <div
            className="date-line"
            key={`${index}-time-date-line`}
          >
            {toLongestDateFormat(message.timetoken)}
          </div>);
      }
      if (index === 0
        || message.entry.id !== messages[index - 1].entry.id
        || toShortTimeFormat(messages[index - 1].timetoken)
        !== toShortTimeFormat(message.timetoken)) {
        nodes.push(
          <div
            className={`time-line ${objectClassName}`}
            key={`${index}-time-time-line`}
          >
            {get(message, 'entry.firstName')}, {toShortTimeFormat(message.timetoken)}
          </div>);
      }
      nodes.push(
        <span
          className={`speech-bubble ${objectClassName}`}
          key={`${index}-content`}
        >
          {texts.map((text, index) => {
            if (text.type === 'url' || text.type === 'email') {
              return (
                <a
                  className="link"
                  href={text.href}
                  key={index}
                  target="_blank"
                >
                  {text.value}
                </a>);
            } else if (text.type === 'imageUrl') {
              return (
                <img
                  alt=""
                  className="image-link"
                  key={index}
                  src={text.href}
                  onClick={() => this.openLightbox(text)}
                />
              );
            }
            return text.value;
          })}
        </span>,
      );
    });
    return nodes;
  }

  render() {
    const { classes, formatMessage } = this.props;
    const {
      chatInput,
      currentContact,
      currentImage,
      lightboxIsOpen,
      lightboxImages,
      minimized,
      notificationTotal,
      showList,
    } = this.state;

    if (minimized) {
      return (
        <div className="chat-box">
          <IconButton
            aria-label="Open"
            classes={{ root: classes.chatIcon }}
            onClick={this.showChatBox}
            variant="fab"
          >
            <Chat />
          </IconButton>
          {Boolean(notificationTotal) && (
            <NotificationBadge classes={{
              badge: { badge: classes.badgeSpan },
              root: { root: classes.badge },
            }}>
              {notificationTotal}
            </NotificationBadge>
          )}
        </div>
      );
    }
    return (
      <div className="chat-box active">
        <div className="chat-header">
          {
            isEmpty(currentContact) ? (
              <div className="left-sided">
                {formatMessage('Messaging')}
              </div>
            ) : (
              <div className="backward">
                <IconButton
                  aria-label="Close"
                  onClick={() => this.setState({
                    currentContact: {},
                    showList: true,
                  })}
                  style={{
                    minHeight: 20,
                    minWidth: 20,
                    padding: 0,
                    transform: 'scale(1.5)',
                  }}
                >
                  <ArrowLeft
                    classes={{ root: classes.backward }}
                    fontSize="default"
                  />
                </IconButton>
              </div>
            )
          }
          {
            isEmpty(currentContact) ? '' : (
              <div className="current-contact-name">
                <span className={`presence ${this.isOnline(currentContact) && 'online'}`} />
                {currentContact.label || currentContact.profile.companyName || `${currentContact.profile.firstName} ${currentContact.profile.lastName}`}
              </div>
            )
          }
          <div className="right-sided">
            {Boolean(notificationTotal) && (
              <NotificationBadge classes={{
                badge: { badge: classes.badgeSpanActive },
                root: { root: classes.topBadge },
              }}>
                {notificationTotal}
              </NotificationBadge>
            )}
            <IconButton
              aria-label="Close"
              onClick={this.hideChatBox}
              size="small"
              style={{
                margin: 5,
                minHeight: 20,
                minWidth: 20,
                width: 20,
              }}
            >
              <Remove classes={{ root: classes.minimizeBtn }} />
            </IconButton>
          </div>
        </div>
        <div className="chat-content">
          {showList ? this.renderList() : this.renderChatRoom()}
        </div>
        {
          !isEmpty(currentContact) && (
            <div className="chat-input">
              <div style={{ padding: 5 }}>
                <IconButton
                  classes={{
                    root: classes.uploadButton,
                  }}
                  onClick={() => this.fileInput.open()}
                >
                  <Add classes={{ root: classes.upload }} />
                </IconButton>
              </div>
              <TextField
                className="text-field"
                InputProps={{ style: { background: 'white', marginTop: -6 } }}
                margin="normal"
                multiline
                onChange={this.handleChange('chatInput')}
                onKeyPress={(e) => {
                  if (!e.shiftKey && e.key === 'Enter') {
                    e.preventDefault();
                    if (e.target.value !== '') {
                      this.sendMessage(e.target.value);
                    }
                  }
                }}
                rowsMax="2"
                style={{
                  flex: 1,
                  margin: '0',
                  overflow: 'hidden',
                  width: '100%',
                }}
                value={chatInput}
                variant="outlined"
              />
              <div>
                <IconButton
                  classes={{
                    disabled: classes.disabledSendButton,
                    root: classes.sendButton,
                  }}
                  disabled={chatInput === ''}
                  onClick={() => this.sendMessage(chatInput)}
                >
                  <Send classes={{ root: classes.send }} />
                </IconButton>
              </div>
            </div>
          )
        }
        <Lightbox
          currentImage={currentImage}
          images={lightboxImages}
          isOpen={lightboxIsOpen}
          onClose={this.closeLightbox}
          showThumbnails
        />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  ...selectState('buyers', 'buyers')(state),
  ...selectState('sellers', 'sellers')(state),
  ...selectState('chats', 'presences')(state),
  ...selectState('auth', 'tokenInfo')(state),
});

const mapDispatchToProps = dispatch => ({
  ...dispatchRestMaker([
    [buyersActions, 'buyer'],
    [membersActions, 'member'],
    [sellersActions, 'seller'],
  ])(dispatch),
  listPresences: payload => dispatch(listPresences(payload)),
  sendMessage: payload => dispatch(sendMessage(payload)),
  messageRead: payload => dispatch(messageRead(payload)),
});

export default compose(
  setDisplayName('ChatBox'),
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withStyles(styles),
  immutableToJS,
  injectIntl,
)(ChatBox);
